import React from 'react';
import { CSSTransition } from 'react-transition-group';
import classnames from 'classnames';

import Icon from '~/components/icon';
import {
  type DrawerMenuItem,
  type MobileHeaderPageStackItem } from
'~/interfaces/HeaderFooterNavV2';

import DrawerMenuLinks from '../drawer-menu-links';
import PromoCodeDropdown from '../menu/promo-code-dropdown';
import SplitNavLinks from '../split-nav-links/split-nav-links';

import styles from './link-with-page.module.css';

interface LinksPage {
  callback?: (text: string) => void;
  hideIcons?: boolean;
  label: string;
  page: DrawerMenuItem;
  pageClick: (item: MobileHeaderPageStackItem) => void;
}

const LinksPage = ({
  callback,
  hideIcons,
  label,
  page: { groups = [], icon, items = [], name, subLinks = [], type },
  pageClick
}: LinksPage): JSX.Element | null => {
  const [showLinksPage, setShowLiksPage] = React.useState(false);

  if (!name) {
    return null;
  }

  const renderSubComponent = (): JSX.Element => {
    const isPromoCodesDropdown = name === 'Promo Codes';
    const isSplitDropdown = type === 'SPLIT_EXPANDABLE';

    // Used for US/CA states
    const renderSplitDropdown = () => {
      return (
        <div className={classnames(styles.page, styles.pageSplitNav)}>
					<SplitNavLinks
            callback={callback}
            label={`${label} - ${name}`}
            links={groups} />

				</div>);

    };

    const renderPromoCodesDropdown = () => {
      return (
        <div className={classnames(styles.page, styles.pagePromoCodes)}>
					<PromoCodeDropdown
            callback={callback}
            label={`${label} - ${name}`}
            links={items} />

				</div>);

    };

    const renderDrawerMenuLinks = () => {
      return (
        <div className={styles.page}>
					<DrawerMenuLinks
            label={`${label} - ${name}`}
            pageClick={pageClick}
            pages={subLinks}
            trackMenu={callback} />

				</div>);

    };

    const renderPage = () => {
      switch (true) {
        case !!isSplitDropdown:
          return renderSplitDropdown();

        case !!isPromoCodesDropdown:
          return renderPromoCodesDropdown();

        default:
          return renderDrawerMenuLinks();
      }
    };

    return (
      <CSSTransition
        classNames={{
          enter: styles.pageEnter,
          enterActive: styles.pageEnterActive,
          enterDone: styles.visible,
          exit: styles.pageExit,
          exitActive: styles.pageExitActive
        }}
        in={showLinksPage}
        timeout={1000}>

				{renderPage()}
			</CSSTransition>);

  };

  const renderIcon = () => {
    if (hideIcons || !icon) return;

    const [iconName, dimension = '16'] = icon.split(',');

    return (
      <div className={styles.iconContainer}>
				<Icon
          className={styles.linkIcon}
          height={parseInt(dimension, 10)}
          icon={iconName}
          width={parseInt(dimension, 10)} />

			</div>);

  };

  const handlePageClick = () => {
    setShowLiksPage((showLinks) => !showLinks);
    pageClick({ label: name, clickEvent: () => setShowLiksPage(false) });
  };

  return (
    <div>
			<div
        className={styles.link}
        data-testid="page-links"
        key={name}
        onClick={handlePageClick}>

				{renderIcon()}
				<div className={styles.linkText}>{name}</div>
				<div className={styles.arrow}></div>
			</div>
			{renderSubComponent()}
		</div>);

};

export default LinksPage;